<script setup>
import pluralize from 'pluralize'
import { Link } from '@inertiajs/vue3'
import { titleCase } from 'title-case'
import Popover from "../Popover.vue";
import Tooltip from "../Tooltip.vue";
import DifficultyIndicator from "./DifficultyIndicator.vue";
import ClockSvg from '@svg/new-clock.svg'

defineProps({
    course: Object,
    topicClasses: String,
    metaClasses: String,
    showDifficulty: {
        type: Boolean,
        default: true
    },
    showType: {
        type: Boolean,
        default: false
    }
})
</script>

<template>
<div class="bg-[rgba(20,22,57,1)] h-[300px] rounded-3xl p-9 flex flex-col justify-between relative">
    <div v-if="showType" class="bg-[rgba(27,32,74,1)] text-[rgba(148,162,209,1)] font-semibold absolute -top-1.5 -right-1.5 rounded-xl px-3.5 py-2 leading-none text-xs">
        {{ titleCase(course.type) }}
    </div>

    <div>
        <div class="flex items-center justify-between">
            <div class="flex items-center space-x-2">
                <div :class="topicClasses" class="rounded-full bg-[rgba(27,32,74,1)] h-8 px-5 flex items-center text-[rgba(186,146,250,1)] font-semibold text-sm" v-for="topic in course.topics" :key="topic.id">
                    {{ topic.title }}
                </div>
            </div>
            <div class="shrink" v-if="showDifficulty">
                <Popover>
                    <DifficultyIndicator :difficulty="course.difficulty" class="p-2 -mr-2" />
                    <template v-slot:popover>
                        <Tooltip class="text-sm text-[rgba(112,120,176,1)] font-medium">
                            Difficulty: <span class="text-[rgba(148,162,209,1)]">{{ titleCase(course.difficulty) }}</span>
                        </Tooltip>
                    </template>
                </Popover>
            </div>
        </div>

        <Link :href="course.type === 'course' ? `/courses/${course.slug}` : `/snippets/${course.slug}`" class="text-[rgba(233,232,255,1)] text-lg md:text-xl font-semibold mt-4 line-clamp-2">
            {{ course.title }}
        </Link>
        <p class="text-[rgba(148,162,209,1)] text-xs line-clamp-3 mt-2 leading-normal">
            {{ course.description }}
        </p>
    </div>
    <div class="flex items-center space-x-2 mt-4">
        <div class="rounded-full bg-[rgba(27,32,74,1)] h-8 px-3 flex items-center text-[rgba(148,162,209,1)] text-xs" :class="metaClasses" v-if="course.parts_count">
            {{ course.parts_count }} {{ pluralize('episode', course.parts_count) }}
        </div>
        <div class="rounded-full bg-[rgba(27,32,74,1)] h-8 px-3 flex items-center text-[rgba(148,162,209,1)] text-xs space-x-1.5" :class="metaClasses">
            <ClockSvg class="size-4" />
            <template v-if="course.upcoming">
                <span>Coming soon</span>
            </template>
            <template v-else>
                <span><template v-if="course.duration.formatted.hours">{{ course.duration.formatted.hours }} {{ pluralize('hr', course.duration.formatted.hours) }} </template> {{ course.duration.formatted.minutes }} {{ pluralize('min', course.duration.formatted.minutes) }}</span>
            </template>
        </div>
    </div>

    <slot name="progress"></slot>
</div>
</template>
